/**
 * One door to the data.
 *
 * Every read and write goes through the Cloudflare Worker at the Pages origin, authenticated with
 * the pairing key in `x-psb-key`. There is no second store and no direct call to Polymarket, ESPN
 * or the AI gateway from here — the client renders what D1 holds and asks the Worker to change it.
 *
 * **Everything here is personal.** The bet ledger is a financial record; there is no anonymous
 * read. A phone cannot hide a secret in a bundle, so the key never ships with the app and lives
 * only on the device that was paired.
 */
import { Platform } from 'react-native';
import { getItem, getSync, removeItem, setItem } from './storage';

export const KEY_STORAGE = 'psb.key';
export const BASE_STORAGE = 'psb.api';

/**
 * Where a build with no origin of its own talks to.
 *
 * The web build is served BY the Worker, so it has a same-origin `/api` and needs none of this. A
 * native build has no origin at all — without a default, a copy opened in Expo Go from a QR code
 * can reach nothing and sits on an unpaired screen it could never leave.
 */
const DEFAULT_API = 'https://polysoccerbets.pages.dev';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/**
 * The origin to send to.
 *
 * On the web this is normally `''` — a relative `/api/…`, same origin, no CORS. The exception is
 * the Expo dev server: at `localhost:5812` a relative call would hit the bundler, which serves no
 * API, so dev is pointed at the local Worker on :5813 instead. `EXPO_PUBLIC_PSB_API` overrides both.
 */
function defaultBase(): string {
  const override = process.env.EXPO_PUBLIC_PSB_API;
  if (override) return override.replace(/\/$/, '');
  if (Platform.OS !== 'web') return DEFAULT_API;

  const host = typeof window === 'undefined' ? '' : window.location.hostname;
  const isDevServer = host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
  return isDevServer ? 'http://127.0.0.1:5813' : '';
}

let key: string | null = getSync(KEY_STORAGE);
let base = defaultBase();

export const api = {
  get key(): string | null {
    return key;
  },
  get base(): string {
    return base;
  },
  /** True once a pairing key exists. Only means a key is present, not that the server likes it. */
  get paired(): boolean {
    return !!key;
  },

  async load(): Promise<void> {
    key = await getItem(KEY_STORAGE);
    if (Platform.OS !== 'web') base = (await getItem(BASE_STORAGE)) || base;
  },

  async pair(newKey: string, apiBase?: string): Promise<void> {
    key = newKey.trim();
    await setItem(KEY_STORAGE, key);
    if (apiBase && Platform.OS !== 'web') {
      base = apiBase.replace(/\/$/, '');
      await setItem(BASE_STORAGE, base);
    }
  },

  async unpair(): Promise<void> {
    key = null;
    await removeItem(KEY_STORAGE);
  },

  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    let res: Response;
    try {
      res = await fetch(`${base}${path}`, {
        method,
        headers: {
          ...(body === undefined ? {} : { 'content-type': 'application/json' }),
          ...(key ? { 'x-psb-key': key } : {}),
          'x-psb-client': Platform.OS,
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      // Status 0 is the browser saying the request never reached anyone — a phone in a lift, not a
      // payload the server refused. Worth telling apart from a rejection.
      throw new ApiError(0, 'Offline — no connection to the server.');
    }

    if (!res.ok) throw new ApiError(res.status, await describe(res));
    if (res.status === 204) return undefined as T;

    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  },

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  },
  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body ?? {});
  },
  patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  },
  put<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  },
};

async function describe(res: Response): Promise<string> {
  if (res.status === 401) return 'Pairing key rejected. Check it in Settings.';
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) return body.error;
  } catch {
    /* not JSON */
  }
  return `${res.status} ${res.statusText}`.trim();
}

/** One message for anything thrown out of {@link api}. */
export function describeError(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return (e as Error)?.message ?? 'Something went wrong.';
}
